/**
 * Recursive-descent parser for the ERA SPARQL-subset query profile.
 *
 * Supported constructs:
 *   SELECT ?vars WHERE { triple_patterns FILTER() OPTIONAL{} }
 *   LIMIT n  OFFSET n  ORDER BY ?var ASC|DESC
 *
 * Explicitly unsupported (throws ParseError with kind='unsupported'):
 *   UNION, SERVICE, property paths, CONSTRUCT, ASK, subqueries,
 *   GRAPH, nested OPTIONAL trees beyond one level
 *
 * The parser contains NO network logic. It only converts strings to ASTs.
 */

import type {
  Query,
  SelectClause,
  WhereClause,
  TriplePattern,
  OptionalPattern,
  FilterExpr,
  LimitClause,
  OffsetClause,
  OrderByClause,
  OrderDirection,
  EraClass,
} from '../ast/index.js';
import { ParseError } from '../ast/index.js';

// ---------------------------------------------------------------------------
// Tokeniser
// ---------------------------------------------------------------------------

type TokenKind =
  | 'KEYWORD'    // SELECT WHERE FILTER OPTIONAL LIMIT OFFSET ORDER BY ASC DESC REGEX CONTAINS LCASE STR LANG
  | 'VAR'        // ?foo
  | 'PREFIXED'   // era:Foo  rdf:type  geo:sfIntersects
  | 'IRI'        // <http://...>
  | 'STRING'     // "…" or '…'
  | 'NUMBER'     // 123 or 123.45
  | 'BOOLEAN'    // true false
  | 'DOT'        // .
  | 'COMMA'      // ,
  | 'SEMICOLON'  // ;
  | 'LBRACE'     // {
  | 'RBRACE'     // }
  | 'LPAREN'     // (
  | 'RPAREN'     // )
  | 'STAR'       // *
  | 'OP'         // = != < > <= >= &&
  | 'A'          // bare 'a' (rdf:type shorthand)
  | 'EOF';

const KEYWORDS = new Set([
  'SELECT', 'WHERE', 'FILTER', 'OPTIONAL', 'LIMIT', 'OFFSET',
  'ORDER', 'BY', 'ASC', 'DESC', 'REGEX', 'CONTAINS', 'LCASE', 'STR', 'LANG',
  'UNION', 'SERVICE', 'CONSTRUCT', 'ASK', 'DESCRIBE', 'GRAPH',
  'PREFIX', 'BASE', 'FROM',
]);

interface Token {
  kind: TokenKind;
  value: string;
  pos: number;
}

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < input.length) {
    // Skip whitespace and comments
    if (/\s/.test(input[i]!)) { i++; continue; }
    if (input[i] === '#') { while (i < input.length && input[i] !== '\n') i++; continue; }

    const start = i;

    // IRI  <http://...>  — but NOT if next char is = (that's <=)
    if (input[i] === '<' && input[i + 1] !== '=') {
      let v = '';
      i++; // skip <
      while (i < input.length && input[i] !== '>') v += input[i++];
      i++; // skip >
      tokens.push({ kind: 'IRI', value: v, pos: start });
      continue;
    }

    // String literals  "..." or '...'
    if (input[i] === '"' || input[i] === "'") {
      const q = input[i]; let v = ''; i++;
      while (i < input.length && input[i] !== q) {
        if (input[i] === '\\') { i++; v += input[i] ?? ''; i++; }
        else { v += input[i++]; }
      }
      i++; // closing quote
      // skip @lang or ^^type suffixes for literals
      if (input[i] === '@') { while (i < input.length && !/[\s,.){}]/.test(input[i]!)) i++; }
      if (input[i] === '^' && input[i+1] === '^') { i += 2; while (i < input.length && !/[\s,.){}]/.test(input[i]!)) i++; }
      tokens.push({ kind: 'STRING', value: v, pos: start });
      continue;
    }

    // Number
    if (/[0-9]/.test(input[i]!) || (input[i] === '-' && /[0-9]/.test(input[i+1] ?? ''))) {
      let v = input[i++] ?? '';
      while (i < input.length && /[0-9.]/.test(input[i]!)) v += input[i++];
      tokens.push({ kind: 'NUMBER', value: v, pos: start });
      continue;
    }

    // Multi-char operators
    if (input.slice(i, i+2) === '<=') { tokens.push({ kind: 'OP', value: '<=', pos: start }); i += 2; continue; }
    if (input.slice(i, i+2) === '>=') { tokens.push({ kind: 'OP', value: '>=', pos: start }); i += 2; continue; }
    if (input.slice(i, i+2) === '!=') { tokens.push({ kind: 'OP', value: '!=', pos: start }); i += 2; continue; }
    if (input.slice(i, i+2) === '&&') { tokens.push({ kind: 'OP', value: '&&', pos: start }); i += 2; continue; }
    if (input.slice(i, i+2) === '||') { tokens.push({ kind: 'OP', value: '||', pos: start }); i += 2; continue; }

    // Single-char punctuation
    const ch = input[i]!;
    if (ch === '=') { tokens.push({ kind: 'OP', value: '=', pos: start }); i++; continue; }
    if (ch === '<') { tokens.push({ kind: 'OP', value: '<', pos: start }); i++; continue; }
    if (ch === '>') { tokens.push({ kind: 'OP', value: '>', pos: start }); i++; continue; }
    if (ch === '.') { tokens.push({ kind: 'DOT', value: '.', pos: start }); i++; continue; }
    if (ch === ',') { tokens.push({ kind: 'COMMA', value: ',', pos: start }); i++; continue; }
    if (ch === ';') { tokens.push({ kind: 'SEMICOLON', value: ';', pos: start }); i++; continue; }
    if (ch === '{') { tokens.push({ kind: 'LBRACE', value: '{', pos: start }); i++; continue; }
    if (ch === '}') { tokens.push({ kind: 'RBRACE', value: '}', pos: start }); i++; continue; }
    if (ch === '(') { tokens.push({ kind: 'LPAREN', value: '(', pos: start }); i++; continue; }
    if (ch === ')') { tokens.push({ kind: 'RPAREN', value: ')', pos: start }); i++; continue; }
    if (ch === '*') { tokens.push({ kind: 'STAR', value: '*', pos: start }); i++; continue; }

    // Variable  ?foo
    if (ch === '?') {
      let v = ''; i++;
      while (i < input.length && /[\w_]/.test(input[i]!)) v += input[i++];
      tokens.push({ kind: 'VAR', value: v, pos: start });
      continue;
    }

    // Prefixed name or keyword or 'a'
    if (/[A-Za-z_]/.test(ch)) {
      let v = '';
      while (i < input.length && /[\w_:\-]/.test(input[i]!)) v += input[i++];
      // Check for prefixed name: era:Foo, rdf:type, geo:sfIntersects
      if (v.includes(':') && !v.startsWith('http')) {
        tokens.push({ kind: 'PREFIXED', value: v, pos: start });
        continue;
      }
      const upper = v.toUpperCase();
      if (v === 'a') { tokens.push({ kind: 'A', value: 'a', pos: start }); continue; }
      if (v === 'true' || v === 'false') { tokens.push({ kind: 'BOOLEAN', value: v, pos: start }); continue; }
      if (KEYWORDS.has(upper)) { tokens.push({ kind: 'KEYWORD', value: upper, pos: start }); continue; }
      tokens.push({ kind: 'PREFIXED', value: v, pos: start });
      continue;
    }

    // Unknown — skip
    i++;
  }

  tokens.push({ kind: 'EOF', value: '', pos: input.length });
  return tokens;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

class Parser {
  private pos = 0;
  constructor(private tokens: Token[]) {}

  private peek(): Token {
    return this.tokens[this.pos] ?? { kind: 'EOF', value: '', pos: -1 };
  }

  private consume(): Token {
    const t = this.peek();
    this.pos++;
    return t;
  }

  private expect(kind: TokenKind, value?: string): Token {
    const t = this.peek();
    if (t.kind !== kind || (value !== undefined && t.value.toUpperCase() !== value.toUpperCase())) {
      throw new ParseError('syntax', `Expected ${value ?? kind} at position ${t.pos}, got "${t.value}"`, t.pos);
    }
    return this.consume();
  }

  private check(kind: TokenKind, value?: string): boolean {
    const t = this.peek();
    return t.kind === kind && (value === undefined || t.value.toUpperCase() === value.toUpperCase());
  }

  parse(): Query {
    // Reject unsupported top-level query types
    if (this.check('KEYWORD', 'PREFIX') || this.check('KEYWORD', 'BASE')) {
      // consume PREFIX/BASE lines before SELECT
      while (!this.check('KEYWORD', 'SELECT') && !this.check('EOF')) this.consume();
    }

    if (this.check('KEYWORD', 'CONSTRUCT') || this.check('KEYWORD', 'ASK') || this.check('KEYWORD', 'DESCRIBE')) {
      throw new ParseError('unsupported', `${this.peek().value} queries are not supported. Use SELECT.`, this.peek().pos);
    }

    this.expect('KEYWORD', 'SELECT');
    const select = this.parseSelect();
    this.expect('KEYWORD', 'WHERE');
    this.expect('LBRACE');
    const where = this.parseWhereClause();
    this.expect('RBRACE');

    let limit: LimitClause | undefined;
    let offset: OffsetClause | undefined;
    let orderBy: OrderByClause | undefined;

    while (!this.check('EOF')) {
      if (this.check('KEYWORD', 'LIMIT')) {
        this.consume();
        const n = this.expect('NUMBER');
        limit = { value: parseInt(n.value, 10) };
      } else if (this.check('KEYWORD', 'OFFSET')) {
        this.consume();
        const n = this.expect('NUMBER');
        offset = { value: parseInt(n.value, 10) };
      } else if (this.check('KEYWORD', 'ORDER')) {
        this.consume();
        this.expect('KEYWORD', 'BY');
        orderBy = this.parseOrderBy();
      } else {
        break;
      }
    }

    // Derive eraClass and primary subject from triple patterns
    const { eraClass, subject } = deriveClassAndSubject(where.patterns);

    return { kind: 'select', select, where, eraClass, subject, limit, offset, orderBy };
  }

  private parseSelect(): SelectClause {
    if (this.check('STAR')) {
      this.consume();
      return { variables: ['*'] };
    }
    const variables: string[] = [];
    while (this.check('VAR')) {
      variables.push(this.consume().value);
    }
    if (variables.length === 0) {
      throw new ParseError('syntax', 'SELECT must be followed by ?variables or *', this.peek().pos);
    }
    return { variables };
  }

  private parseWhereClause(): WhereClause {
    const patterns: TriplePattern[] = [];
    const optionals: OptionalPattern[] = [];
    const filters: FilterExpr[] = [];

    while (!this.check('RBRACE') && !this.check('EOF')) {
      if (this.check('KEYWORD', 'OPTIONAL')) {
        this.consume();
        this.expect('LBRACE');
        const inner: TriplePattern[] = [];
        while (!this.check('RBRACE') && !this.check('EOF')) {
          if (this.check('KEYWORD', 'FILTER')) {
            // filters inside OPTIONAL are treated as top-level filters
            this.consume();
            this.expect('LPAREN');
            filters.push(this.parseFilterExpr());
            this.expect('RPAREN');
          } else {
            const tp = this.parseTripleOrSemicolon();
            inner.push(...tp);
          }
        }
        this.expect('RBRACE');
        optionals.push({ patterns: inner });
        continue;
      }

      if (this.check('KEYWORD', 'UNION')) {
        throw new ParseError('unsupported', 'UNION is not supported in this query profile. Use separate queries.', this.peek().pos);
      }

      if (this.check('KEYWORD', 'SERVICE')) {
        throw new ParseError('unsupported', 'SERVICE (federated queries) is not supported in this query profile.', this.peek().pos);
      }

      if (this.check('KEYWORD', 'GRAPH')) {
        throw new ParseError('unsupported', 'GRAPH is not supported in this query profile.', this.peek().pos);
      }

      if (this.check('KEYWORD', 'FILTER')) {
        this.consume();
        this.expect('LPAREN');
        filters.push(this.parseFilterExpr());
        this.expect('RPAREN');
        continue;
      }

      // Nested group { ... } — consume to check for UNION; either way unsupported
      if (this.check('LBRACE')) {
        this.consume();
        let depth = 1;
        while (depth > 0 && !this.check('EOF')) {
          if (this.check('LBRACE')) depth++;
          if (this.check('RBRACE')) depth--;
          this.consume();
        }
        if (this.check('KEYWORD', 'UNION')) {
          throw new ParseError('unsupported', 'UNION is not supported in this query profile. Use separate queries.', this.peek().pos);
        }
        throw new ParseError('unsupported', 'Nested groups are not supported in this query profile.', this.peek().pos);
      }

      const tp = this.parseTripleOrSemicolon();
      patterns.push(...tp);
    }

    return { patterns, optionals, filters };
  }

  /**
   * Parse one or more triples sharing the same subject (semicolon-separated predicates)
   * or a single triple, terminated by '.' or '}'.
   */
  private parseTripleOrSemicolon(): TriplePattern[] {
    const subject = this.parseTerm();
    const results: TriplePattern[] = [];

    let predicate = this.parsePredicate();
    let object = this.parseTerm();
    results.push({ subject, predicate, object });

    while (this.check('SEMICOLON')) {
      this.consume();
      if (this.check('RBRACE') || this.check('DOT') || this.check('EOF')) break;
      predicate = this.parsePredicate();
      object = this.parseTerm();
      results.push({ subject, predicate, object });
    }

    if (this.check('COMMA')) {
      // Object list: ?s pred ?o1, ?o2
      while (this.check('COMMA')) {
        this.consume();
        const obj2 = this.parseTerm();
        results.push({ subject, predicate, object: obj2 });
      }
    }

    // Optional dot terminator
    if (this.check('DOT')) this.consume();

    return results;
  }

  private parseTerm(): string {
    const t = this.peek();
    if (t.kind === 'VAR') { this.consume(); return `?${t.value}`; }
    if (t.kind === 'PREFIXED') { this.consume(); return t.value; }
    if (t.kind === 'IRI') { this.consume(); return `<${t.value}>`; }
    if (t.kind === 'STRING') { this.consume(); return `"${t.value}"`; }
    if (t.kind === 'NUMBER') { this.consume(); return t.value; }
    if (t.kind === 'BOOLEAN') { this.consume(); return t.value; }
    if (t.kind === 'A') { this.consume(); return 'a'; }
    throw new ParseError('syntax', `Expected a term (variable, prefixed name, IRI, literal), got "${t.value}" at pos ${t.pos}`, t.pos);
  }

  private parsePredicate(): string {
    const t = this.peek();
    if (t.kind === 'A') { this.consume(); return 'a'; }
    if (t.kind === 'PREFIXED') { this.consume(); return t.value; }
    if (t.kind === 'IRI') { this.consume(); return `<${t.value}>`; }
    if (t.kind === 'VAR') { this.consume(); return `?${t.value}`; }
    throw new ParseError('syntax', `Expected a predicate at pos ${t.pos}, got "${t.value}"`, t.pos);
  }

  /** Parse the expression inside FILTER(...) — already consumed '(' */
  private parseFilterExpr(): FilterExpr {
    const t = this.peek();

    // CONTAINS(LCASE(?var), "value") — case-insensitive contains
    if (t.kind === 'KEYWORD' && t.value === 'CONTAINS') {
      return this.parseContainsFilter();
    }

    // REGEX(?var, "pattern") or REGEX(?var, "pattern", "flags")
    if (t.kind === 'KEYWORD' && t.value === 'REGEX') {
      return this.parseRegexFilter();
    }

    // STR(?var) = "value" or LCASE(STR(?var)) = ...
    if ((t.kind === 'KEYWORD' && (t.value === 'STR' || t.value === 'LCASE'))) {
      return this.parseStrFilter();
    }

    // ?var op value  or  value op ?var
    if (t.kind === 'VAR') {
      return this.parseComparisonFilter();
    }

    // Nested: CONTAINS(...) && CONTAINS(...)  — not supported
    if (t.kind === 'LPAREN') {
      throw new ParseError(
        'unsupported',
        'Compound FILTER expressions with && or || are not supported. ' +
          'Split each condition into a separate FILTER clause.',
        t.pos,
      );
    }

    throw new ParseError('syntax', `Unsupported FILTER expression starting with "${t.value}" at pos ${t.pos}`, t.pos);
  }

  private parseContainsFilter(): FilterExpr {
    this.expect('KEYWORD', 'CONTAINS');
    this.expect('LPAREN');
    // CONTAINS(LCASE(?var), "value") or CONTAINS(?var, "value")
    let caseInsensitive = false;
    let variable = '';
    let property = '';

    if (this.check('KEYWORD', 'LCASE') || this.check('KEYWORD', 'STR')) {
      caseInsensitive = true;
      this.consume();
      this.expect('LPAREN');
      const vt = this.parseTerm(); // ?var or prefixed
      variable = stripLeadingQuestion(vt);
      property = extractProperty(vt);
      this.expect('RPAREN');
    } else {
      const vt = this.parseTerm();
      variable = stripLeadingQuestion(vt);
      property = extractProperty(vt);
    }
    this.expect('COMMA');
    const val = this.expect('STRING');
    this.expect('RPAREN');

    return { kind: 'contains', variable, property, value: val.value, caseInsensitive };
  }

  private parseRegexFilter(): FilterExpr {
    this.expect('KEYWORD', 'REGEX');
    this.expect('LPAREN');
    const vt = this.parseTerm();
    const variable = stripLeadingQuestion(vt);
    const property = extractProperty(vt);
    this.expect('COMMA');
    const pattern = this.expect('STRING');
    let flags = '';
    if (this.check('COMMA')) {
      this.consume();
      flags = this.expect('STRING').value;
    }
    this.expect('RPAREN');
    return { kind: 'regex', variable, property, pattern: pattern.value, flags };
  }

  private parseStrFilter(): FilterExpr {
    // STR(?var) = "val" or LCASE(STR(?var)) = "val"
    let caseInsensitive = false;
    if (this.check('KEYWORD', 'LCASE')) {
      caseInsensitive = true;
      this.consume();
      this.expect('LPAREN');
    }
    if (this.check('KEYWORD', 'STR')) this.consume();
    this.expect('LPAREN');
    const vt = this.parseTerm();
    const variable = stripLeadingQuestion(vt);
    this.expect('RPAREN');
    if (caseInsensitive) this.expect('RPAREN');
    const op = this.expect('OP');
    const valT = this.peek();
    let value: string | number | boolean;
    if (valT.kind === 'STRING') { this.consume(); value = valT.value; }
    else if (valT.kind === 'NUMBER') { this.consume(); value = parseFloat(valT.value); }
    else if (valT.kind === 'BOOLEAN') { this.consume(); value = valT.value === 'true'; }
    else throw new ParseError('syntax', `Expected a value after ${op.value}`, valT.pos);

    if (op.value === '=') {
      return { kind: 'equality', variable, property: variable, value };
    }
    if (op.value === 'CONTAINS' || op.value === '~') {
      return { kind: 'contains', variable, property: variable, value: String(value), caseInsensitive };
    }
    throw new ParseError('syntax', `Unsupported operator ${op.value} in STR filter`, op.pos);
  }

  private parseComparisonFilter(): FilterExpr {
    const vt = this.parseTerm();
    const variable = stripLeadingQuestion(vt);
    const property = extractProperty(vt);
    const op = this.peek();

    if (op.kind !== 'OP') {
      throw new ParseError('syntax', `Expected operator after ${vt} at pos ${op.pos}`, op.pos);
    }
    this.consume();

    const valT = this.peek();
    let value: string | number | boolean;
    if (valT.kind === 'STRING') { this.consume(); value = valT.value; }
    else if (valT.kind === 'NUMBER') { this.consume(); value = parseFloat(valT.value); }
    else if (valT.kind === 'BOOLEAN') { this.consume(); value = valT.value === 'true'; }
    else if (valT.kind === 'PREFIXED') { this.consume(); value = valT.value; }
    else throw new ParseError('syntax', `Expected value after operator at pos ${valT.pos}`, valT.pos);

    // handle && chaining — collect ranges
    if (op.value === '>' || op.value === '>=' || op.value === '<' || op.value === '<=') {
      const rangeFilter: { min?: number; max?: number } = {};
      applyRangeBound(rangeFilter, op.value, Number(value));

      // Try to consume another bound connected by &&
      if (this.check('OP', '&&')) {
        this.consume();
        if (this.check('VAR')) {
          const vt2 = this.parseTerm();
          const prop2 = extractProperty(vt2);
          if (prop2 === property || stripLeadingQuestion(vt2) === variable) {
            const op2 = this.consume();
            const val2 = this.consume();
            applyRangeBound(rangeFilter, op2.value, parseFloat(val2.value));
          }
        }
      }

      return { kind: 'range', variable, property, ...rangeFilter };
    }

    if (op.value === '=') {
      return { kind: 'equality', variable, property, value };
    }

    throw new ParseError('syntax', `Unsupported comparison operator "${op.value}"`, op.pos);
  }

  private parseOrderBy(): OrderByClause {
    let direction: OrderDirection = 'ASC';
    if (this.check('KEYWORD', 'ASC') || this.check('KEYWORD', 'DESC')) {
      direction = this.consume().value.toUpperCase() as OrderDirection;
      this.expect('LPAREN');
      const v = this.expect('VAR');
      this.expect('RPAREN');
      return { variable: v.value, direction };
    }
    const v = this.expect('VAR');
    return { variable: v.value, direction };
  }

}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stripLeadingQuestion(term: string): string {
  return term.startsWith('?') ? term.slice(1) : term;
}

/** Extract a short property name from a variable or prefixed term */
function extractProperty(term: string): string {
  if (term.startsWith('?')) return term.slice(1);
  const colonIdx = term.lastIndexOf(':');
  if (colonIdx >= 0) return term.slice(colonIdx + 1);
  return term;
}

function applyRangeBound(
  range: { min?: number; max?: number },
  op: string,
  value: number,
): void {
  if (op === '>' || op === '>=') range.min = value;
  else if (op === '<' || op === '<=') range.max = value;
}

/** Scan triple patterns for `?x a era:ClassName` to determine the queried ERA class */
function deriveClassAndSubject(
  patterns: TriplePattern[],
): { eraClass: EraClass | null; subject: string | null } {
  const ERA_CLASSES: Record<string, EraClass> = {
    'OperationalPoint': 'OperationalPoint',
    'SectionOfLine': 'SectionOfLine',
    'Track': 'Track',
    'RunningTrack': 'RunningTrack',
    'PlatformEdge': 'PlatformEdge',
    'Tunnel': 'Tunnel',
    'Bridge': 'Bridge',
    'ContactLineSystem': 'ContactLineSystem',
  };

  for (const tp of patterns) {
    if (tp.predicate === 'a' || tp.predicate === 'rdf:type') {
      const localName = tp.object.includes(':') ? tp.object.split(':')[1]! : tp.object;
      const cls = ERA_CLASSES[localName];
      if (cls) {
        return {
          eraClass: cls,
          subject: tp.subject.startsWith('?') ? tp.subject.slice(1) : tp.subject,
        };
      }
    }
  }
  return { eraClass: null, subject: null };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parse a SPARQL-subset ERA query string into an AST `Query`.
 *
 * Throws `ParseError` on syntax errors (kind='syntax') or unsupported
 * SPARQL constructs (kind='unsupported').
 *
 * The parser contains no network logic.
 */
export function parse(query: string): Query {
  const tokens = tokenize(query);
  const parser = new Parser(tokens);
  return parser.parse();
}

/** @internal Exposed for debugging */
export { tokenize };
